import React, { useState } from "react";
import { Box, Button, Modal, Switch, TextField } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { useSnackbar } from "notistack";
import { FormattedMessage, useIntl } from "react-intl";
import Invitation from "./Invitation";
import sh from "bundles/common/utils/sh";
import ConfirmModal from "bundles/common/components/ConfirmModal";
import { replaceCurrentRecruiter } from "bundles/Recruiter/constants/recruiterConstants.js";

const Team = () => {
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const authenticityToken = useSelector((state) => state.authenticity_token);
  const i18nLocale = useSelector((state) => state.i18nLocale);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const intl = useIntl();

  const teamMembers = (currentRecruiter?.company?.recruiters || []).filter(
    (member) => member.id !== currentRecruiter.id
  );

  const acceptedMembers = teamMembers.filter(
    (member) =>
      member.invitation_accepted_at ||
      (!member.invitation_created_at && !member.invitation_sent_at) ||
      member.last_sign_in_at
  );

  const pendingInvitations = teamMembers.filter(
    (member) => member.invitation_sent_at && !member.invitation_accepted_at
  );

  const recipientCandidates = [currentRecruiter, ...acceptedMembers];

  const [isModalOpen, setModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [recruiterToRevoke, setRecruiterToRevoke] = useState(null);
  const [notifiedIds, setNotifiedIds] = useState(
    currentRecruiter?.company?.application_notification_recruiter_ids || []
  );
  const [extraEmail, setExtraEmail] = useState(
    currentRecruiter?.company?.application_notification_email || ""
  );
  const [savingEmail, setSavingEmail] = useState(false);

  const handleToggleNotified = (memberId) => {
    const previousIds = notifiedIds;
    const newIds = previousIds.includes(memberId)
      ? previousIds.filter((id) => id !== memberId)
      : [...previousIds, memberId];
    setNotifiedIds(newIds);

    sh.put("/recruiter/application_notification_recruiters", {
      authenticity_token: authenticityToken,
      recruiter_ids: newIds,
    })
      .then(() => {
        replaceCurrentRecruiter(dispatch, authenticityToken);
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.invitations.recipients_updated" }),
          { variant: "success" }
        );
      })
      .catch((err) => {
        console.error(err);
        setNotifiedIds(previousIds);
        enqueueSnackbar("Oops... Something went wrong", { variant: "error" });
      });
  };

  const handleSaveExtraEmail = () => {
    setSavingEmail(true);
    sh.put("/recruiter/application_notification_recruiters", {
      authenticity_token: authenticityToken,
      email: extraEmail.trim(),
    })
      .then(() => {
        replaceCurrentRecruiter(dispatch, authenticityToken);
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.invitations.recipients_updated" }),
          { variant: "success" }
        );
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(
          intl.formatMessage({
            id: "settings.invitations.recipients_email_error",
          }),
          { variant: "error" }
        );
      })
      .finally(() => setSavingEmail(false));
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleResend = (invite) => {
    sh.post("/recruiter/resend_invitation", {
      authenticity_token: authenticityToken,
      recruiter: invite,
    })
      .then(() => {
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.invitations.invitation_resent" }),
          {
            variant: "success",
          }
        );
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar("Oops... Something went wrong", {
          variant: "error",
        });
      });
  };

  const handleRevoke = (invite) => {
    setRecruiterToRevoke(invite);
    setIsConfirmModalOpen(true);
  };

  const handleRevokeConfirmed = (invite) => {
    sh.get(`/${i18nLocale}/recruiters/invitation/remove?id=${invite.id}`)
      .then(() => {
        replaceCurrentRecruiter(dispatch, authenticityToken);
        enqueueSnackbar(
          intl.formatMessage({ id: "settings.invitations.invitation_revoked" }),
          {
            variant: "success",
          }
        );
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar("Oops... Something went wrong", {
          variant: "error",
        });
      });
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          mb: "24px",
        }}
      >
        <Box
          component="h2"
          sx={{
            fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            fontSize: "1.25rem",
            color: "hsl(178, 93%, 10%)",
            letterSpacing: "-0.01em",
            m: 0,
          }}
        >
          <FormattedMessage id="settings.team" />
        </Box>
        <Button
          variant="rounded"
          size="small"
          onClick={handleOpenModal}
          sx={{
            minWidth: "150px",
            textTransform: "none",
            px: 1.75,
            py: 0.75,
            fontSize: "0.95rem",
            fontFamily: "Segoe UI Semibold, sans-serif",
            backgroundColor: "hsl(153 68% 25%)",
            color: "white",
            boxShadow: "none",
            transition: "all 180ms ease",
            "&:hover": {
              backgroundColor: "hsl(153 68% 22%)",
              boxShadow: "none",
            },
          }}
        >
          <FormattedMessage id="settings.invitations.add_team_member" />
        </Button>
      </Box>

      {/* Team members */}
      <Box
        sx={{
          border: "1px solid hsl(220, 13%, 91%)",
          borderRadius: "12px",
          overflow: "hidden",
        }}
      >
        {recipientCandidates.map((member, index) => {
          const isYou = member.id === currentRecruiter.id;
          return (
            <Box
              key={member.id}
              sx={{
                display: "flex",
                alignItems: "center",
                px: "16px",
                py: "12px",
                borderTop:
                  index === 0 ? "none" : "1px solid hsl(220, 13%, 91%)",
              }}
            >
              <Box
                component="span"
                sx={{
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontSize: "0.9375rem",
                  color: "hsl(220, 25%, 12%)",
                }}
              >
                {member.first_name} {member.last_name}
                {isYou && (
                  <>
                    {" "}
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        color: "hsl(220, 10%, 46%)",
                      }}
                    >
                      (<FormattedMessage id="settings.invitations.you" />)
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Notification recipients */}
      <Box sx={{ mt: "40px" }}>
        <Box
          component="h3"
          sx={{
            fontFamily: "Segoe UI Bold, system-ui, sans-serif",
            fontSize: "1.0625rem",
            color: "hsl(220, 25%, 12%)",
            m: 0,
            mb: "4px",
          }}
        >
          <FormattedMessage id="settings.invitations.recipients_title" />
        </Box>
        <Box
          component="p"
          sx={{
            fontFamily: "Segoe UI Regular, system-ui, sans-serif",
            fontSize: "0.875rem",
            color: "hsl(220, 10%, 46%)",
            m: 0,
            mb: "16px",
          }}
        >
          <FormattedMessage id="settings.invitations.recipients_hint" />
        </Box>

        {notifiedIds.length === 0 && (
          <Box
            sx={{
              bgcolor: "hsl(210, 20%, 98%)",
              border: "1px solid hsl(220, 13%, 91%)",
              borderRadius: "10px",
              px: "16px",
              py: "12px",
              mb: "16px",
            }}
          >
            <Box
              component="span"
              sx={{
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                fontSize: "0.875rem",
                color: "hsl(220, 25%, 12%)",
              }}
            >
              <FormattedMessage id="settings.invitations.recipients_default" />
            </Box>
          </Box>
        )}

        <Box
          sx={{
            border: "1px solid hsl(220, 13%, 91%)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          {recipientCandidates.map((member, index) => {
            const isYou = member.id === currentRecruiter.id;
            return (
              <Box
                key={member.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: "16px",
                  py: "12px",
                  borderTop:
                    index === 0 ? "none" : "1px solid hsl(220, 13%, 91%)",
                }}
              >
                <Box>
                  <Box
                    sx={{
                      fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                      fontSize: "0.9375rem",
                      color: "hsl(220, 25%, 12%)",
                    }}
                  >
                    {member.first_name} {member.last_name}
                    {isYou && (
                      <>
                        {" "}
                        <Box
                          component="span"
                          sx={{
                            fontFamily:
                              "Segoe UI Regular, system-ui, sans-serif",
                            color: "hsl(220, 10%, 46%)",
                          }}
                        >
                          (<FormattedMessage id="settings.invitations.you" />)
                        </Box>
                      </>
                    )}
                  </Box>
                  <Box
                    sx={{
                      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                      fontSize: "0.8125rem",
                      color: "hsl(220, 10%, 46%)",
                    }}
                  >
                    {member.email}
                  </Box>
                </Box>
                <Switch
                  color="secondary"
                  checked={notifiedIds.includes(member.id)}
                  onChange={() => handleToggleNotified(member.id)}
                />
              </Box>
            );
          })}
        </Box>

        <Box sx={{ mt: "16px" }}>
          <Box
            component="p"
            sx={{
              fontFamily: "Segoe UI Regular, system-ui, sans-serif",
              fontSize: "0.875rem",
              color: "hsl(220, 10%, 46%)",
              m: 0,
              mb: "8px",
            }}
          >
            <FormattedMessage id="settings.invitations.recipients_email_hint" />
          </Box>
          <Box
            sx={{
              display: "flex",
              gap: "12px",
              alignItems: "flex-start",
              flexWrap: "wrap",
            }}
          >
            <TextField
              value={extraEmail}
              onChange={(e) => setExtraEmail(e.target.value)}
              label={
                <FormattedMessage id="settings.invitations.recipients_email_label" />
              }
              placeholder="rh@company.com"
              variant="outlined"
              size="small"
              type="email"
              sx={{ flex: "1 1 240px", minWidth: "200px" }}
            />
            <Button
              variant="rounded"
              size="small"
              onClick={handleSaveExtraEmail}
              disabled={savingEmail}
              sx={{
                textTransform: "none",
                px: 2,
                py: 0.9,
                fontFamily: "Segoe UI Semibold, sans-serif",
                backgroundColor: "hsl(153 68% 25%)",
                color: "white",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "hsl(153 68% 22%)",
                  boxShadow: "none",
                },
              }}
            >
              <FormattedMessage id="settings.invitations.recipients_email_save" />
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Pending invitations */}
      {pendingInvitations.length > 0 && (
        <Box sx={{ mt: "40px" }}>
          <Box
            component="h3"
            sx={{
              fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              fontSize: "1.0625rem",
              color: "hsl(220, 25%, 12%)",
              m: 0,
              mb: "16px",
            }}
          >
            <FormattedMessage id="settings.invitations.pending_invitations" />
          </Box>
          <Box
            sx={{
              border: "1px solid hsl(220, 13%, 91%)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            {pendingInvitations.map((invite, index) => (
              <Box
                key={invite.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "12px",
                  px: "16px",
                  py: "12px",
                  borderTop:
                    index === 0 ? "none" : "1px solid hsl(220, 13%, 91%)",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                    fontSize: "0.9375rem",
                    color: "hsl(220, 25%, 12%)",
                  }}
                >
                  {invite.first_name} {invite.last_name}
                </Box>
                <Box>
                  <Button
                    variant="rounded"
                    color="primaryOutlined"
                    size="small"
                    onClick={() => handleResend(invite)}
                    style={{ marginRight: "12px", textTransform: "none" }}
                  >
                    <FormattedMessage id="settings.invitations.resend" />
                  </Button>
                  <Button
                    variant="rounded"
                    color="dangerOutlined"
                    size="small"
                    onClick={() => handleRevoke(invite)}
                    style={{ textTransform: "none" }}
                  >
                    <FormattedMessage id="settings.invitations.revoke" />
                  </Button>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Invitation Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Invitation onClose={handleCloseModal} />
      </Modal>
      {/* Confirm Revoke Modal */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        setIsOpen={setIsConfirmModalOpen}
        handleConfirm={() => handleRevokeConfirmed(recruiterToRevoke)}
        labelId="settings.invitations.delete_invitation"
      />
    </Box>
  );
};

export default Team;
